'use client';

import React, { useState, useEffect } from 'react';
import {
    Search,
    Download,
    Calendar,
    ArrowLeft,
    CheckCircle2,
    XCircle,
    Filter,
    FileText,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Clock,
    ExternalLink,
    Copy,
    Check,
    Eye,
    Sliders,
    Maximize2,
    X,
    ImageIcon,
    Sparkles,
    ArrowRight
} from 'lucide-react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ImagePreviewModal, ImagePreviewRecord } from '../components/ImagePreviewModal';

export function Logs() {
    const { user } = useAuth();
    const [logs, setLogs] = useState<ImagePreviewRecord[]>([]);
    const [filteredLogs, setFilteredLogs] = useState<ImagePreviewRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<'all' | 'images_only' | 'success' | 'failed'>('all');
    const [selectedLogForModal, setSelectedLogForModal] = useState<ImagePreviewRecord | null>(null);
    const [copiedUrlId, setCopiedUrlId] = useState<string | null>(null);

    // Stats
    const [stats, setStats] = useState({
        total: 0,
        success: 0,
        failed: 0,
        totalCost: 0,
        withImages: 0
    });

    useEffect(() => {
        if (user?.userId) {
            fetchLogs();
        }
    }, [user?.userId]);

    useEffect(() => {
        filterLogs();
    }, [logs, searchTerm, filterStatus]);

    const fetchLogs = async () => {
        if (!user?.userId) return;

        try {
            setLoading(true);
            const logsRef = collection(db, 'bgusers', user.userId, 'api_usage');
            const q = query(logsRef, orderBy('timestamp', 'desc'));
            const querySnapshot = await getDocs(q);

            const fetchedLogs: ImagePreviewRecord[] = [];
            let successCount = 0;
            let failedCount = 0;
            let costSum = 0;
            let imageCount = 0;

            querySnapshot.forEach((doc: any) => {
                const data = doc.data();
                const log: ImagePreviewRecord = {
                    id: doc.id,
                    timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp ? new Date(data.timestamp) : new Date()),
                    endpoint: data.endpoint || '',
                    success: data.success ?? true,
                    error: data.error,
                    file_name: data.file_name || data.fileName,
                    original_url: data.original_url || data.originalUrl || data.input_url || data.inputUrl,
                    result_url: data.result_url || data.resultUrl || data.output_url || data.outputUrl || data.processedUrl,
                    cost: data.cost || 0,
                    duration_ms: data.duration_ms
                };
                fetchedLogs.push(log);

                if (log.success) successCount++;
                else failedCount++;
                if (log.cost) costSum += log.cost;
                if (log.original_url || log.result_url) imageCount++;
            });

            setLogs(fetchedLogs);
            setStats({
                total: fetchedLogs.length,
                success: successCount,
                failed: failedCount,
                totalCost: costSum,
                withImages: imageCount
            });
        } catch (error) {
            console.error("Error fetching logs:", error);
        } finally {
            setLoading(false);
        }
    };

    const filterLogs = () => {
        let result = [...logs];

        // Search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(log =>
                (log.file_name && log.file_name.toLowerCase().includes(term)) ||
                (log.error && log.error.toLowerCase().includes(term)) ||
                (log.endpoint && log.endpoint.toLowerCase().includes(term)) ||
                (log.original_url && log.original_url.toLowerCase().includes(term)) ||
                (log.result_url && log.result_url.toLowerCase().includes(term)) ||
                (log.id && log.id.toLowerCase().includes(term))
            );
        }

        // Status filter
        if (filterStatus === 'success') {
            result = result.filter(log => log.success);
        } else if (filterStatus === 'failed') {
            result = result.filter(log => !log.success);
        } else if (filterStatus === 'images_only') {
            result = result.filter(log => Boolean(log.original_url || log.result_url));
        }

        setFilteredLogs(result);
    };

    const copyToClipboard = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedUrlId(id);
        setTimeout(() => setCopiedUrlId(null), 2000);
    };

    const downloadPDF = () => {
        const doc = new jsPDF();

        // Header
        doc.setFontSize(20);
        doc.setTextColor(40, 40, 40);
        doc.text("API Usage Logs Report", 14, 22);

        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);
        doc.text(`User Email: ${user?.email || 'N/A'}`, 14, 35);

        // Stats Summary
        doc.setFillColor(240, 240, 240);
        doc.rect(14, 40, 180, 25, 'F');
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text(`Total Calls: ${stats.total}`, 20, 52);
        doc.text(`Successful: ${stats.success}`, 70, 52);
        doc.text(`Failed: ${stats.failed}`, 120, 52);
        doc.text(`With Images: ${stats.withImages}`, 160, 52);

        // Table
        const tableColumn = ["Time", "File Name", "Endpoint", "Status", "Input URL", "Output URL"];

        const tableRows = filteredLogs.map(log => {
            return [
                log.timestamp.toLocaleString(),
                log.file_name || 'N/A',
                log.endpoint || '-',
                log.success ? 'Success' : 'Failed',
                log.original_url ? 'Available' : '-',
                log.result_url ? 'Available' : '-'
            ];
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 70,
            theme: 'grid',
            headStyles: { fillColor: [59, 130, 246] },
            styles: { fontSize: 8 }
        });

        doc.save(`api-logs-report-${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const formatTime = (date: Date) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }).format(date);
    };

    return (
        <div className="min-h-screen bg-background py-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Top Bar */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                    <div className="flex items-center gap-4">
                        <Link to="/dashboard" className="p-2 hover:bg-muted rounded-full transition-colors">
                            <ArrowLeft className="h-6 w-6 text-muted-foreground" />
                        </Link>
                        <div>
                            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                                API Logs & History
                                <span className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">
                                    Live Previews
                                </span>
                            </h1>
                            <p className="text-muted-foreground text-sm mt-1">
                                Complete usage logs with Input and Output image previews, URLs, and stats
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={downloadPDF}
                            disabled={loading || filteredLogs.length === 0}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm shadow-sm"
                        >
                            <Download className="h-4 w-4" />
                            Download Report
                        </button>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
                        <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider font-medium">Total API Requests</p>
                        <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
                        <div className="flex items-center gap-1.5 mb-1">
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Successful</p>
                        </div>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-500">{stats.success}</p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
                        <div className="flex items-center gap-1.5 mb-1">
                            <ImageIcon className="h-3.5 w-3.5 text-primary" />
                            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Images Processed</p>
                        </div>
                        <p className="text-2xl font-bold text-primary">{stats.withImages}</p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
                        <div className="flex items-center gap-1.5 mb-1">
                            <XCircle className="h-3.5 w-3.5 text-red-500" />
                            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Failed Calls</p>
                        </div>
                        <p className="text-2xl font-bold text-red-600 dark:text-red-500">{stats.failed}</p>
                    </div>
                </div>

                {/* Filters & Search Toolbar */}
                <div className="bg-card border border-border rounded-t-xl p-4 flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search by filename, endpoint, ID, or image URL..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-muted-foreground" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value as any)}
                            className="bg-background border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer"
                        >
                            <option value="all">All Logs ({logs.length})</option>
                            <option value="images_only">With Image Previews ({stats.withImages})</option>
                            <option value="success">Success Only ({stats.success})</option>
                            <option value="failed">Failed Only ({stats.failed})</option>
                        </select>
                    </div>
                </div>

                {/* Logs List Container */}
                <div className="bg-card border border-border border-t-0 rounded-b-xl overflow-hidden flex flex-col min-h-[500px]">
                    <div className="p-4 space-y-3 overflow-y-auto max-h-[750px] custom-scrollbar">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
                                <Loader2 className="h-8 w-8 text-primary animate-spin" />
                                <p className="text-sm">Loading API logs and previews...</p>
                            </div>
                        ) : filteredLogs.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                                <FileText className="h-12 w-12 mb-3 opacity-40" />
                                <p className="font-medium text-foreground">No logs found</p>
                                <p className="text-xs text-muted-foreground mt-1">Try changing your search query or filter</p>
                            </div>
                        ) : (
                            filteredLogs.map((log) => {
                                const hasImages = Boolean(log.original_url || log.result_url);

                                return (
                                    <div
                                        key={log.id}
                                        className="bg-background border border-border rounded-xl p-4 hover:border-primary/40 transition-all shadow-sm hover:shadow-md flex flex-col gap-4"
                                    >
                                        {/* Log Item Header */}
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-3">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-1.5 rounded-full ${log.success ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                                                    {log.success ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-semibold text-foreground text-sm">
                                                            {log.file_name || (
                                                                log.endpoint === '/api/v1/key-info' ? 'Key Info Check' :
                                                                    log.endpoint === '/api/v1/usage' ? 'Usage Stats Check' :
                                                                        'API Request'
                                                            )}
                                                        </span>
                                                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted font-mono text-muted-foreground border border-border">
                                                            {log.endpoint}
                                                        </span>
                                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${log.success ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-red-500/10 text-red-500'}`}>
                                                            {log.success ? '200 OK' : 'Failed'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" />
                                                    {formatTime(log.timestamp)}
                                                </span>
                                                {log.duration_ms !== undefined && (
                                                    <span className="flex items-center gap-1 font-mono">
                                                        <Clock className="h-3 w-3 text-primary" />
                                                        {log.duration_ms < 1000 ? `${log.duration_ms}ms` : `${(log.duration_ms / 1000).toFixed(2)}s`}
                                                    </span>
                                                )}
                                                {log.id && (
                                                    <span className="font-mono text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                                        ID: {log.id.slice(0, 8)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Image Preview & URL Section */}
                                        {hasImages ? (
                                            <div className="bg-card/70 border border-border/70 rounded-lg p-3.5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                                <div className="flex items-center gap-4 flex-wrap">
                                                    {/* Input Preview */}
                                                    {log.original_url && (
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                onClick={() => setSelectedLogForModal(log)}
                                                                className="group relative w-16 h-16 rounded-lg overflow-hidden border border-border bg-background cursor-pointer hover:ring-2 hover:ring-primary transition-all flex-shrink-0"
                                                            >
                                                                <img
                                                                    src={log.original_url}
                                                                    alt="Original"
                                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                                                                />
                                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                    <Eye className="w-4 h-4 text-white" />
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <span className="text-xs font-medium text-muted-foreground block">Input Image</span>
                                                                <div className="flex items-center gap-1.5">
                                                                    <button
                                                                        onClick={() => copyToClipboard(log.original_url!, `orig-${log.id}`)}
                                                                        className="px-2 py-1 text-[11px] font-medium bg-muted hover:bg-muted/80 text-foreground rounded border border-border transition-colors flex items-center gap-1"
                                                                        title="Copy Input Image URL"
                                                                    >
                                                                        {copiedUrlId === `orig-${log.id}` ? (
                                                                            <Check className="w-3 h-3 text-green-500" />
                                                                        ) : (
                                                                            <Copy className="w-3 h-3 text-muted-foreground" />
                                                                        )}
                                                                        {copiedUrlId === `orig-${log.id}` ? 'Copied' : 'Copy URL'}
                                                                    </button>
                                                                    <a
                                                                        href={log.original_url}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="p-1 text-muted-foreground hover:text-foreground rounded hover:bg-muted transition-colors"
                                                                        title="Open in new tab"
                                                                    >
                                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {log.original_url && log.result_url && (
                                                        <ArrowRight className="w-4 h-4 text-muted-foreground hidden sm:block" />
                                                    )}

                                                    {/* Output Preview */}
                                                    {log.result_url && (
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                onClick={() => setSelectedLogForModal(log)}
                                                                className="group relative w-16 h-16 rounded-lg overflow-hidden border border-green-500/30 bg-[linear-gradient(45deg,#262626_25%,transparent_25%),linear-gradient(-45deg,#262626_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#262626_75%),linear-gradient(-45deg,transparent_75%,#262626_75%)] bg-[size:10px_10px] bg-[#171717] cursor-pointer hover:ring-2 hover:ring-green-500 transition-all flex-shrink-0"
                                                            >
                                                                <img
                                                                    src={log.result_url}
                                                                    alt="Result"
                                                                    className="w-full h-full object-contain p-0.5 group-hover:scale-110 transition-transform duration-200"
                                                                />
                                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                    <Eye className="w-4 h-4 text-white" />
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <span className="text-xs font-medium text-green-600 dark:text-green-400 block flex items-center gap-1">
                                                                    <Sparkles className="w-3 h-3" /> Output (No BG)
                                                                </span>
                                                                <div className="flex items-center gap-1.5">
                                                                    <button
                                                                        onClick={() => copyToClipboard(log.result_url!, `res-${log.id}`)}
                                                                        className="px-2 py-1 text-[11px] font-medium bg-muted hover:bg-muted/80 text-foreground rounded border border-border transition-colors flex items-center gap-1"
                                                                        title="Copy Output Image URL"
                                                                    >
                                                                        {copiedUrlId === `res-${log.id}` ? (
                                                                            <Check className="w-3 h-3 text-green-500" />
                                                                        ) : (
                                                                            <Copy className="w-3 h-3 text-muted-foreground" />
                                                                        )}
                                                                        {copiedUrlId === `res-${log.id}` ? 'Copied' : 'Copy URL'}
                                                                    </button>
                                                                    <a
                                                                        href={log.result_url}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="p-1 text-muted-foreground hover:text-foreground rounded hover:bg-muted transition-colors"
                                                                        title="Open in new tab"
                                                                    >
                                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Full Preview Button */}
                                                <button
                                                    onClick={() => setSelectedLogForModal(log)}
                                                    className="w-full md:w-auto px-3.5 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                                                >
                                                    <Maximize2 className="w-3.5 h-3.5" /> Compare & View Details
                                                </button>
                                            </div>
                                        ) : (
                                            log.error && (
                                                <p className="text-xs text-red-500 bg-red-500/10 px-3 py-2 rounded-lg border border-red-500/20 font-mono">
                                                    Error: {log.error}
                                                </p>
                                            )
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* Lightbox / Comparison Modal */}
            {selectedLogForModal && (
                <ImagePreviewModal
                    record={selectedLogForModal}
                    onClose={() => setSelectedLogForModal(null)}
                />
            )}
        </div>
    );
}
